import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n.jsx";
import { Check } from "lucide-react";
import api from "@/lib/api";

const AR_DOW = ["أح", "اث", "ثل", "أر", "خم", "جم", "سب"];
const EN_DOW = ["Sn","Mo","Tu","We","Th","Fr","Sa"];

/**
 * StreakModal — celebratory streak overview modal.
 *
 * Design:
 * - Circular emblem with the 🔥 emoji + polka-dot backdrop
 * - Huge streak-days number
 * - Encouraging line addressed to the user by first name
 * - 7 pill markers for the current week: orange check for logged days,
 *   muted date-numeric for future days
 * - Big orange "إغلاق" button at the bottom
 */
export default function StreakModal({ open, onClose, user }) {
  const { lang } = useI18n();
  const DOW = lang === "en" ? EN_DOW : AR_DOW;
  const [days, setDays] = useState([]);
  const [streak, setStreak] = useState(user?.streak_days || 0);

  useEffect(() => {
    if (!open) return;
    api.get("/streak/week").then(({ data }) => {
      setDays(Array.isArray(data?.days) ? data.days : []);
      setStreak(data?.streak_days ?? user?.streak_days ?? 0);
    }).catch(() => {});
  }, [open, user?.streak_days]);

  if (!open) return null;

  const firstName = (user?.full_name || "").trim().split(" ")[0] || (lang === "en" ? "Champ" : "بطلي");

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
      onClick={onClose}
      dir="rtl"
      data-testid="streak-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-[32px] shadow-card-lg p-6 pt-4 animate-pop"
        data-testid="streak-modal"
      >
        {/* Emblem */}
        <div className="flex justify-center -mt-14 mb-3">
          <div
            className="w-28 h-28 rounded-full bg-white shadow-card flex items-center justify-center relative overflow-hidden"
            data-testid="streak-emblem"
          >
            {/* polka-dot backdrop */}
            <div className="absolute inset-0 pointer-events-none">
              <span className="absolute top-3 left-4 w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
              <span className="absolute top-5 right-6 w-2 h-2 rounded-full bg-emerald-300/70" />
              <span className="absolute bottom-6 right-3 w-3 h-3 rounded-full bg-emerald-400/70" />
              <span className="absolute bottom-4 left-7 w-2 h-2 rounded-full bg-emerald-300/70" />
              <span className="absolute top-10 right-10 w-1.5 h-1.5 rounded-full bg-emerald-500/60" />
            </div>
            <span className="text-6xl relative z-10">🔥</span>
          </div>
        </div>

        {/* Streak count */}
        <div className="text-center mb-2">
          <div className="text-6xl font-black text-neutral-900 num leading-none" data-testid="streak-count">
            {streak}
          </div>
        </div>
        <div className="text-center mb-1">
          <span className="text-2xl font-bold text-neutral-900">{lang === "en" ? "days" : "أيام"} </span>
          <span className="text-2xl font-black text-brand-500 italic">{lang === "en" ? "in a row!" : "متتالية!"}</span>
        </div>
        <p className="text-center text-sm text-neutral-500 mb-5 leading-relaxed">
          {lang === "en" ? `Great work ${firstName} — keep logging daily!` : `أداؤك رائع يا ${firstName} — استمر في التسجيل يومياً!`}
        </p>

        {/* Weekly dots */}
        <div className="grid grid-cols-7 gap-2 mb-6" data-testid="streak-week">
          {days.map((d, i) => {
            const dayLabel = DOW[d.day_of_week];
            const dayNum = parseInt(d.date.slice(-2), 10);
            return (
              <div key={d.date} className="flex flex-col items-center gap-1.5">
                <div className={`text-[11px] font-semibold ${d.is_today ? "text-brand-500" : "text-neutral-400"}`}>
                  {dayLabel}
                </div>
                {d.is_logged ? (
                  <div
                    className="w-9 h-9 rounded-full orange-gradient shadow-brand flex items-center justify-center"
                    data-testid={`streak-day-${i}-logged`}
                  >
                    <Check className="w-5 h-5 text-white" strokeWidth={3.5} />
                  </div>
                ) : (
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center num text-sm font-bold ${
                      d.is_future ? "text-neutral-300" : "text-neutral-400 bg-neutral-100"
                    }`}
                    data-testid={`streak-day-${i}-empty`}
                  >
                    {dayNum}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={onClose}
          className="w-full orange-gradient text-white font-bold py-3.5 rounded-full shadow-brand active:scale-[0.98] transition-transform"
          data-testid="streak-close-btn"
        >
          {lang === "en" ? "Close" : "إغلاق"}
        </button>
      </div>
    </div>
  );
}
